/**
 * Admin Panel Type Definitions
 * Part of TASK-334: Frontend Admin Panel
 *
 * Types for user management, role management, team management,
 * account settings, security settings, and audit logs.
 */

// ==================== User Management Types ====================

export type UserRole = 'admin' | 'advisor' | 'viewer' | 'compliance_officer';
export type UserStatus = 'active' | 'pending' | 'deactivated';
export type InvitationStatus = 'invited' | 'pending' | 'accepted' | 'expired' | 'revoked';

export interface AdminUser {
  id: string;
  account_id?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  avatar_url?: string;
  phone?: string;
  role: UserRole;
  is_active: boolean;
  is_email_verified?: boolean;
  invitation_status?: InvitationStatus;
  last_login_at?: string;
  password_changed_at?: string;
  must_change_password?: boolean;
  preferred_language?: string;
  timezone?: string;
  created_at?: string;
  updated_at?: string;
}

export interface UserListItem {
  id: string;
  email: string;
  first_name?: string;
  last_name?: string;
  display_name?: string;
  role: UserRole;
  is_active: boolean;
  invitation_status?: InvitationStatus;
  last_login_at?: string;
  created_at?: string;
}

export interface UserListResponse {
  users: UserListItem[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface UserCreateData {
  email: string;
  first_name?: string;
  last_name?: string;
  role: UserRole;
  send_invitation?: boolean;
}

export interface UserUpdateData {
  first_name?: string;
  last_name?: string;
  phone?: string;
  preferred_language?: string;
  timezone?: string;
}

export interface UserRoleUpdateData {
  role: UserRole;
}

export interface UserStatusUpdateData {
  is_active: boolean;
}

export interface UserInvitation {
  id: string;
  email: string;
  role: UserRole;
  status: InvitationStatus;
  expires_at: string;
  invited_by_user_id: string;
  invited_by_name?: string;
  created_at: string;
}

export interface UserInvitationListResponse {
  invitations: UserInvitation[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface InviteUserData {
  email: string;
  role: UserRole;
}

// ==================== Role & Permission Types ====================

export interface PermissionAction {
  action: string;
  description: string;
}

export interface PermissionResource {
  resource: string;
  description: string;
  actions: PermissionAction[];
}

export interface PermissionDefinitions {
  resources: PermissionResource[];
  all_permissions: string[];
}

export interface Role {
  id: string;
  account_id: string;
  name: string;
  description?: string;
  permissions: string[];
  is_system_role: boolean;
  is_default: boolean;
  user_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface RoleListResponse {
  roles: Role[];
  total: number;
}

export interface RoleCreateData {
  name: string;
  description?: string;
  permissions: string[];
  is_default?: boolean;
}

export interface RoleUpdateData {
  name?: string;
  description?: string;
  permissions?: string[];
  is_default?: boolean;
}

export interface RoleDuplicateData {
  new_name: string;
  new_description?: string;
}

// Permission matrix helper types
export interface PermissionGroup {
  resource: string;
  description: string;
  permissions: {
    action: string;
    description: string;
    permission: string;
    enabled: boolean;
  }[];
}

// ==================== Team Management Types ====================

export type TeamMemberRole = 'lead' | 'member';

export interface TeamMemberSummary {
  user_id: string;
  email: string;
  display_name?: string;
  role: TeamMemberRole;
  user_role: UserRole;
  joined_at?: string;
}

export interface Team {
  id: string;
  account_id: string;
  name: string;
  description?: string;
  lead_user_id?: string;
  lead_display_name?: string;
  is_active: boolean;
  member_count: number;
  client_count: number;
  created_at?: string;
  updated_at?: string;
}

export interface TeamDetail extends Team {
  members: TeamMemberSummary[];
}

export interface TeamListResponse {
  teams: Team[];
  total: number;
}

export interface TeamCreateData {
  name: string;
  description?: string;
  lead_user_id?: string;
}

export interface TeamUpdateData {
  name?: string;
  description?: string;
  lead_user_id?: string;
  is_active?: boolean;
}

export interface TeamMemberAddData {
  user_id: string;
  role?: TeamMemberRole;
}

export interface TeamClientAssignment {
  client_id: string;
  client_name: string;
  assigned_at: string;
  assigned_by?: string;
}

// ==================== Account Settings Types ====================

export type ClientVisibilityMode = 'open' | 'team_based' | 'assigned_only' | 'chinese_wall';

export interface AccountSettings {
  account_id: string;
  client_visibility_mode: ClientVisibilityMode;
  company_name?: string;
  company_logo_url?: string;
  primary_color?: string;
  default_timezone: string;
  default_language: string;
  date_format: string;
  enable_team_management: boolean;
  enable_client_restrictions: boolean;
  sensitive_data_view_roles: string[];
  created_at?: string;
  updated_at?: string;
}

export interface AccountSettingsUpdateData {
  client_visibility_mode?: ClientVisibilityMode;
  company_name?: string;
  company_logo_url?: string;
  primary_color?: string;
  default_timezone?: string;
  default_language?: string;
  date_format?: string;
  enable_team_management?: boolean;
  enable_client_restrictions?: boolean;
  sensitive_data_view_roles?: string[];
}

export interface VisibilityModeOption {
  mode: ClientVisibilityMode;
  name: string;
  description: string;
  requires_teams: boolean;
}

export interface VisibilityModesResponse {
  options: VisibilityModeOption[];
  current_mode: ClientVisibilityMode;
}

// ==================== Security Settings Types ====================

export interface SecuritySettings {
  account_id: string;
  // Password policy
  min_password_length: number;
  require_uppercase: boolean;
  require_lowercase: boolean;
  require_numbers: boolean;
  require_special_chars: boolean;
  password_expiry_days?: number;
  password_history_count: number;
  // Session settings
  session_timeout_minutes: number;
  max_concurrent_sessions?: number;
  // Lockout settings
  max_failed_attempts: number;
  lockout_duration_minutes: number;
  // 2FA settings
  require_2fa_for_admin: boolean;
  require_2fa_for_all: boolean;
  allowed_2fa_methods: string[];
  // IP restriction
  ip_whitelist: string[];
  enable_ip_restriction: boolean;
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export interface SecuritySettingsUpdateData {
  min_password_length?: number;
  require_uppercase?: boolean;
  require_lowercase?: boolean;
  require_numbers?: boolean;
  require_special_chars?: boolean;
  password_expiry_days?: number | null;
  password_history_count?: number;
  session_timeout_minutes?: number;
  max_concurrent_sessions?: number | null;
  max_failed_attempts?: number;
  lockout_duration_minutes?: number;
  require_2fa_for_admin?: boolean;
  require_2fa_for_all?: boolean;
  allowed_2fa_methods?: string[];
  ip_whitelist?: string[];
  enable_ip_restriction?: boolean;
}

// ==================== Audit Log Types ====================

export type AuditSeverity = 'debug' | 'info' | 'warning' | 'error' | 'critical';
export type AuditExportFormat = 'csv' | 'json';

export interface AuditLogEntry {
  id: string;
  event_type: string;
  action: string;
  severity: AuditSeverity;
  user_id?: string;
  user_email?: string;
  user_role?: string;
  resource_type?: string;
  resource_id?: string;
  resource_name?: string;
  client_id?: string;
  client_name?: string;
  changes?: Record<string, unknown>;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
}

export interface AuditLogDetailResponse {
  log: AuditLogEntry;
  checksum?: string;
  checksum_valid?: boolean;
}

export interface AuditLogListResponse {
  logs: AuditLogEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_more: boolean;
}

export interface AuditFilter {
  user_id?: string;
  event_type?: string;
  event_types?: string[];
  resource_type?: string;
  resource_id?: string;
  client_id?: string;
  severity?: AuditSeverity;
  date_from?: string;
  date_to?: string;
  ip_address?: string;
  search?: string;
  page?: number;
  page_size?: number;
  last_id?: string;
}

export interface AuditExportOptions {
  format: AuditExportFormat;
  user_id?: string;
  event_types?: string[];
  date_from?: string;
  date_to?: string;
  severity?: AuditSeverity;
  resource_type?: string;
}

export interface AuditEventTypesResponse {
  event_types: string[];
  categories: Record<string, string[]>;
}

// Audit reports
export interface UserActivitySummary {
  user_id: string;
  user_email: string;
  display_name?: string;
  total_actions: number;
  last_active?: string;
  login_count: number;
  clients_accessed: number;
  changes_made: number;
}

export interface UserActivityReportResponse {
  users: UserActivitySummary[];
  total: number;
  period_start: string;
  period_end: string;
}

export interface BlockedAccessEntry {
  id: string;
  user_id: string;
  user_email: string;
  client_id: string;
  client_name: string;
  reason: string;
  restriction_type: string;
  created_at: string;
}

export interface BlockedAccessReportResponse {
  entries: BlockedAccessEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
}

export interface LoginHistoryEntry {
  id: string;
  user_id?: string;
  user_email?: string;
  event_type: string;
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  is_suspicious: boolean;
}

export interface LoginHistoryReportResponse {
  entries: LoginHistoryEntry[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  suspicious_count: number;
}

// ==================== UI Helper Types ====================

export interface AdminNavItem {
  label: string;
  path: string;
  icon: string;
  permission?: string;
}

export interface AdminBreadcrumb {
  label: string;
  path?: string;
}

// Status display helpers
export const USER_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  pending: 'Pending',
  deactivated: 'Deactivated',
};

export const USER_STATUS_COLORS: Record<string, string> = {
  active: 'bg-green-100 text-green-800',
  pending: 'bg-amber-100 text-amber-800',
  deactivated: 'bg-gray-100 text-gray-600',
};

export const INVITATION_STATUS_LABELS: Record<InvitationStatus, string> = {
  invited: 'Invited',
  pending: 'Pending',
  accepted: 'Accepted',
  expired: 'Expired',
  revoked: 'Revoked',
};

export const INVITATION_STATUS_COLORS: Record<InvitationStatus, string> = {
  invited: 'bg-blue-100 text-blue-800',
  pending: 'bg-amber-100 text-amber-800',
  accepted: 'bg-green-100 text-green-800',
  expired: 'bg-red-100 text-red-800',
  revoked: 'bg-gray-100 text-gray-600',
};

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Admin',
  advisor: 'Advisor',
  viewer: 'Viewer',
  compliance_officer: 'Compliance Officer',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: 'Full access to all features including user management',
  advisor: 'Client management, meetings, portfolios, and compliance workflows',
  viewer: 'Read-only access to clients, portfolios, and reports',
  compliance_officer: 'Reviews and approves KYC profiles, manages compliance workflows',
};

export const SEVERITY_LABELS: Record<AuditSeverity, string> = {
  debug: 'Debug',
  info: 'Info',
  warning: 'Warning',
  error: 'Error',
  critical: 'Critical',
};

export const SEVERITY_COLORS: Record<AuditSeverity, string> = {
  debug: 'bg-gray-100 text-gray-600',
  info: 'bg-blue-100 text-blue-800',
  warning: 'bg-amber-100 text-amber-800',
  error: 'bg-red-100 text-red-800',
  critical: 'bg-red-200 text-red-900',
};

export const VISIBILITY_MODE_LABELS: Record<ClientVisibilityMode, string> = {
  open: 'Open Access',
  team_based: 'Team-Based',
  assigned_only: 'Assigned Only',
  chinese_wall: 'Chinese Wall',
};

export const VISIBILITY_MODE_DESCRIPTIONS: Record<ClientVisibilityMode, string> = {
  open: 'All users can view all clients in the account',
  team_based: 'Users can view clients assigned to their team',
  assigned_only: 'Users can only view clients explicitly assigned to them',
  chinese_wall: 'Strict restrictions with explicit allow/deny rules per client',
};

// ==================== Client Assignment Types ====================

export type ClientAssignmentType = 'primary' | 'secondary' | 'viewer';

export interface ClientAssignment {
  client_id: string;
  client_name?: string;
  client_type?: string;
  assignment_type: ClientAssignmentType;
  assigned_at?: string;
  assigned_by?: string;
}

export interface UserClientsResponse {
  user_id: string;
  clients: ClientAssignment[];
  total: number;
}

export interface AssignClientRequest {
  user_id: string;
  assignment_type?: ClientAssignmentType;
  notes?: string;
}

export const ASSIGNMENT_TYPE_LABELS: Record<ClientAssignmentType, string> = {
  primary: 'Primary',
  secondary: 'Secondary',
  viewer: 'Viewer',
};

export const ASSIGNMENT_TYPE_DESCRIPTIONS: Record<ClientAssignmentType, string> = {
  primary: 'Main advisor responsible for the client',
  secondary: 'Supporting advisor with full access',
  viewer: 'Read-only access to client information',
};

// ==================== AI Settings Types (TASK-335) ====================

export type PIIMaskingMode = 'redact' | 'pseudonymize' | 'hash';
export type PIISensitivity = 'low' | 'medium' | 'high';
export type AIPersonality = 'professional' | 'friendly' | 'formal';
export type RestrictionType = 'forbidden' | 'disclaimer_required' | 'limited';
export type AIInteractionType = 'chat' | 'meeting_assist' | 'document_analysis' | 'form_prefill' | 'brief_generation' | 'search' | 'other';
export type AIRiskLevel = 'standard' | 'elevated' | 'high';

export interface AISettings {
  id: string;
  account_id: string;
  // Feature toggles
  ai_enabled: boolean;
  ai_chat_enabled: boolean;
  ai_meeting_assistant_enabled: boolean;
  ai_document_analysis_enabled: boolean;
  ai_form_prefill_enabled: boolean;
  ai_brief_generation_enabled: boolean;
  // Data access controls
  ai_can_access_client_data: boolean;
  ai_can_access_portfolio_data: boolean;
  ai_can_access_meeting_transcripts: boolean;
  ai_can_access_documents: boolean;
  ai_sensitive_data_access: boolean;
  // PII masking
  mask_personal_identifiers: boolean;
  mask_financial_data: boolean;
  mask_contact_info: boolean;
  mask_demographic_data: boolean;
  pii_masking_mode: PIIMaskingMode;
  // Compliance settings
  investment_advice_allowed: boolean;
  tax_advice_allowed: boolean;
  legal_advice_allowed: boolean;
  require_disclaimer: boolean;
  disclaimer_text?: string;
  eu_ai_act_compliant: boolean;
  gdpr_compliant: boolean;
  // Output filtering
  scan_output_for_pii: boolean;
  block_pii_in_output: boolean;
  pii_detection_sensitivity: PIISensitivity;
  // Behavior
  ai_personality: AIPersonality;
  ai_language: string;
  custom_instructions?: string;
  forbidden_topics?: string[];
  // Model & performance
  preferred_model: string;
  max_tokens_per_response: number;
  temperature: number;
  // Rate limiting
  max_requests_per_user_per_hour: number;
  max_requests_per_account_per_day: number;
  // Logging
  log_ai_interactions: boolean;
  log_retention_days: number;
  anonymize_logs: boolean;
  log_compliance_flags: boolean;
  // Timestamps
  created_at?: string;
  updated_at?: string;
}

export interface AISettingsUpdateData {
  ai_enabled?: boolean;
  ai_chat_enabled?: boolean;
  ai_meeting_assistant_enabled?: boolean;
  ai_document_analysis_enabled?: boolean;
  ai_form_prefill_enabled?: boolean;
  ai_brief_generation_enabled?: boolean;
  ai_can_access_client_data?: boolean;
  ai_can_access_portfolio_data?: boolean;
  ai_can_access_meeting_transcripts?: boolean;
  ai_can_access_documents?: boolean;
  ai_sensitive_data_access?: boolean;
  mask_personal_identifiers?: boolean;
  mask_financial_data?: boolean;
  mask_contact_info?: boolean;
  mask_demographic_data?: boolean;
  pii_masking_mode?: PIIMaskingMode;
  investment_advice_allowed?: boolean;
  tax_advice_allowed?: boolean;
  legal_advice_allowed?: boolean;
  require_disclaimer?: boolean;
  disclaimer_text?: string;
  eu_ai_act_compliant?: boolean;
  gdpr_compliant?: boolean;
  scan_output_for_pii?: boolean;
  block_pii_in_output?: boolean;
  pii_detection_sensitivity?: PIISensitivity;
  ai_personality?: AIPersonality;
  ai_language?: string;
  custom_instructions?: string;
  forbidden_topics?: string[];
  preferred_model?: string;
  max_tokens_per_response?: number;
  temperature?: number;
  max_requests_per_user_per_hour?: number;
  max_requests_per_account_per_day?: number;
  log_ai_interactions?: boolean;
  log_retention_days?: number;
  anonymize_logs?: boolean;
  log_compliance_flags?: boolean;
}

export interface TopicRestriction {
  id: string;
  account_id: string;
  topic: string;
  restriction_type: RestrictionType;
  custom_response?: string;
  created_at?: string;
  created_by_user_id?: string;
}

export interface TopicRestrictionCreateData {
  topic: string;
  restriction_type: RestrictionType;
  custom_response?: string;
}

export interface AIInteractionLog {
  id: string;
  account_id: string;
  user_id: string;
  user_email_hash?: string;
  interaction_type: AIInteractionType;
  model_used?: string;
  client_id?: string;
  meeting_id?: string;
  document_id?: string;
  prompt_category?: string;
  prompt_summary?: string;
  response_category?: string;
  response_summary?: string;
  tokens_used?: number;
  pii_detected_in_prompt: boolean;
  pii_types_detected?: string[];
  pii_masked_count: number;
  pii_detected_in_output: boolean;
  pii_blocked_in_output: boolean;
  disclaimers_shown: boolean;
  disclaimer_type?: string;
  restricted_topics_detected?: string[];
  compliance_flags?: string[];
  human_oversight_required: boolean;
  risk_level: AIRiskLevel;
  guardrails_applied?: string[];
  user_agent_category?: string;
  response_time_ms?: number;
  checksum?: string;
  created_at: string;
}

export interface AIInteractionLogListResponse {
  logs: AIInteractionLog[];
  total: number;
  page: number;
  page_size: number;
  total_pages: number;
  has_more: boolean;
}

export interface AIInteractionLogFilter {
  user_id?: string;
  interaction_type?: AIInteractionType;
  risk_level?: AIRiskLevel;
  pii_detected?: boolean;
  date_from?: string;
  date_to?: string;
  page?: number;
  page_size?: number;
}

export interface AIUsageStats {
  account_id: string;
  period_start: string;
  period_end: string;
  total_interactions: number;
  total_tokens_used: number;
  unique_users: number;
  interactions_by_type: Record<string, number>;
  daily_interactions: Array<{ date: string; interactions: number; tokens: number }>;
  pii_detected_count: number;
  pii_masked_count: number;
  pii_blocked_in_output_count: number;
  compliance_flags_triggered: number;
  disclaimers_shown: number;
  restricted_topics_detected: number;
  high_risk_interactions: number;
  elevated_risk_interactions: number;
  rate_limit_hits: number;
}

export interface AIFeatureStatus {
  ai_enabled: boolean;
  ai_chat_enabled: boolean;
  ai_meeting_assistant_enabled: boolean;
  ai_document_analysis_enabled: boolean;
  ai_form_prefill_enabled: boolean;
  ai_brief_generation_enabled: boolean;
  any_feature_enabled: boolean;
  all_features_enabled: boolean;
}

// AI Settings UI Helper Constants
export const PII_MASKING_MODE_LABELS: Record<PIIMaskingMode, string> = {
  redact: 'Redact',
  pseudonymize: 'Pseudonymize',
  hash: 'Hash',
};

export const PII_MASKING_MODE_DESCRIPTIONS: Record<PIIMaskingMode, string> = {
  redact: 'Replace with type-specific placeholders (e.g., [SSN_REDACTED])',
  pseudonymize: 'Replace with realistic fake values',
  hash: 'Replace with truncated SHA-256 hash values',
};

export const PII_SENSITIVITY_LABELS: Record<PIISensitivity, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
};

export const PII_SENSITIVITY_DESCRIPTIONS: Record<PIISensitivity, string> = {
  low: 'Detect only critical PII (SSN, credit cards)',
  medium: 'Detect common PII patterns (names, addresses, phones)',
  high: 'Aggressive detection including partial matches',
};

export const AI_PERSONALITY_LABELS: Record<AIPersonality, string> = {
  professional: 'Professional',
  friendly: 'Friendly',
  formal: 'Formal',
};

export const AI_PERSONALITY_DESCRIPTIONS: Record<AIPersonality, string> = {
  professional: 'Formal, business-focused communication style',
  friendly: 'Approachable, conversational communication style',
  formal: 'Highly formal, conservative communication style',
};

export const RESTRICTION_TYPE_LABELS: Record<RestrictionType, string> = {
  forbidden: 'Forbidden',
  disclaimer_required: 'Disclaimer Required',
  limited: 'Limited',
};

export const RESTRICTION_TYPE_DESCRIPTIONS: Record<RestrictionType, string> = {
  forbidden: 'AI will refuse to discuss this topic',
  disclaimer_required: 'AI will discuss with mandatory disclaimer',
  limited: 'AI will provide reduced detail on this topic',
};

export const AI_INTERACTION_TYPE_LABELS: Record<AIInteractionType, string> = {
  chat: 'Chat',
  meeting_assist: 'Meeting Assistant',
  document_analysis: 'Document Analysis',
  form_prefill: 'Form Prefill',
  brief_generation: 'Brief Generation',
  search: 'Search',
  other: 'Other',
};

export const AI_RISK_LEVEL_LABELS: Record<AIRiskLevel, string> = {
  standard: 'Standard',
  elevated: 'Elevated',
  high: 'High',
};

export const AI_RISK_LEVEL_COLORS: Record<AIRiskLevel, string> = {
  standard: 'bg-green-100 text-green-800',
  elevated: 'bg-amber-100 text-amber-800',
  high: 'bg-red-100 text-red-800',
};

export const AI_LANGUAGE_OPTIONS = [
  { value: 'en', label: 'English' },
  { value: 'de', label: 'German' },
  { value: 'fr', label: 'French' },
  { value: 'it', label: 'Italian' },
];

// ==================== ASR (Speech-to-Text) Settings — TASK-426 ====================

export type ASRProvider = 'openai' | 'local';

/** Per-tenant ASR configuration (accounts.asr_provider/asr_model/asr_language). */
export interface ASRSettings {
  asr_provider: ASRProvider;
  asr_model: string;
  /** BCP-47 locale, or null for auto-detect. */
  asr_language: string | null;
}

/** PUT body — all fields optional; pass asr_language null for auto-detect. */
export interface ASRSettingsUpdateData {
  asr_provider?: ASRProvider;
  asr_model?: string;
  asr_language?: string | null;
}

export const ASR_PROVIDER_OPTIONS: { value: ASRProvider; label: string; description: string }[] = [
  { value: 'openai', label: 'OpenAI Whisper (cloud)', description: 'Audio is sent to OpenAI for transcription.' },
  { value: 'local', label: 'On-prem (FINMA)', description: 'Audio is transcribed entirely on our infrastructure and never sent to OpenAI.' },
];

/** Allowed models per provider — mirrors the auth-service /api/admin/asr/models. */
export const ASR_MODEL_OPTIONS: Record<ASRProvider, { value: string; label: string }[]> = {
  openai: [
    { value: 'whisper-1', label: 'whisper-1' },
    { value: 'gpt-4o-transcribe', label: 'gpt-4o-transcribe' },
  ],
  local: [
    { value: 'large-v3', label: 'large-v3 (standard, multilingual)' },
    { value: 'whisper-swiss-german', label: 'Swiss German fine-tune (de-CH)' },
  ],
};

/** '' is sent to the API as null (auto-detect). */
export const ASR_LANGUAGE_OPTIONS = [
  { value: '', label: 'Auto-detect' },
  { value: 'de-CH', label: 'Swiss German (de-CH)' },
  { value: 'fr-CH', label: 'French — Switzerland (fr-CH)' },
  { value: 'it-CH', label: 'Italian — Switzerland (it-CH)' },
  { value: 'en', label: 'English (en)' },
];

// ==================== Tenant Feature Flags — TASK-428 ====================

/** Per-tenant UI feature flags (accounts.features JSONB). Permissive when empty. */
export type TenantFeatures = Record<string, boolean>;

/** GET/PUT body shape for /api/admin/features. */
export interface TenantFeaturesData {
  features: TenantFeatures;
}

/**
 * The set of gateable cockpit features surfaced in the admin toggle UI.
 * `key` must match the id a `visibleFor` predicate reads from
 * `ctx.tenant.features` in the cockpit feature registry.
 */
export const FEATURE_TOGGLE_OPTIONS: { key: string; label: string; description: string }[] = [
  {
    key: 'askAI',
    label: 'Ask AI panel',
    description: 'In-meeting AI assistant panel (user-initiated Q&A about the client, portfolio and documents).',
  },
  {
    key: 'portfolioTab',
    label: 'Portfolio tab',
    description: 'Show the Portfolio view in the meeting cockpit.',
  },
  {
    key: 'proposalsTab',
    label: 'Proposals tab',
    description: 'Show the Proposals view in the meeting cockpit.',
  },
];
